import React, { useState } from 'react';
import { 
  User, 
  Calendar, 
  Clock, 
  DollarSign, 
  CheckCircle, 
  AlertCircle, 
  Users, 
  FileText, 
  Mail, 
  Phone,
  MapPin,
  Award,
  TrendingUp,
  Coffee,
  Car,
  Home
} from 'lucide-react';

const EmployeeDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');

  // Dummy employee data
  const employeeData = {
    profile: {
      name: "Priya Sharma",
      id: "KK001",
      position: "Senior Software Developer",
      department: "Engineering",
      email: "priya.sharma@kodekalp.com",
      phone: "+91 98765 43210",
      location: "Mumbai, India",
      joinDate: "January 15, 2022",
      manager: "Rahul Verma",
      profileImage: "https://images.unsplash.com/photo-1494790108755-2616b612b5bc?w=150&h=150&fit=crop&crop=face"
    },
    attendance: {
      todayStatus: "Present",
      checkInTime: "09:15 AM",
      checkOutTime: "--",
      totalHours: "7.5h",
      weeklyHours: "37.5h",
      monthlyAttendance: 95,
      leavesUsed: 8,
      leavesRemaining: 22
    },
    projects: [
      {
        name: "ERP System Redesign",
        status: "In Progress",
        progress: 75,
        deadline: "Dec 31, 2025",
        priority: "high"
      },
      {
        name: "Mobile App Development",
        status: "In Progress", 
        progress: 45,
        deadline: "Jan 15, 2026",
        priority: "medium"
      },
      {
        name: "API Documentation",
        status: "Completed",
        progress: 100,
        deadline: "Nov 30, 2025",
        priority: "low"
      }
    ],
    payroll: {
      basicSalary: "₹85,000",
      totalEarnings: "₹1,02,000",
      deductions: "₹12,000",
      netSalary: "₹90,000",
      lastPayDate: "November 30, 2025",
      ytdEarnings: "₹10,80,000"
    },
    team: [
      { name: "Arjun Kumar", role: "Frontend Developer", avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face" },
      { name: "Sneha Patel", role: "UI/UX Designer", avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=40&h=40&fit=crop&crop=face" },
      { name: "Vikram Singh", role: "Backend Developer", avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face" }
    ],
    announcements: [
      {
        title: "Year-end Party Announcement",
        content: "Join us for the annual celebration on December 28th at 6 PM",
        date: "Dec 15, 2025",
        priority: "medium"
      },
      {
        title: "New Health Insurance Policy",
        content: "Updated health benefits are now available. Check your email for details.",
        date: "Dec 10, 2025",
        priority: "high"
      }
    ]
  };

  const TabButton = ({ id, label, icon: Icon }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
        activeTab === id 
          ? 'bg-blue-600 text-white shadow-lg' 
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      <Icon size={18} />
      {label}
    </button>
  );

  const StatCard = ({ icon: Icon, title, value, subtitle, color = "blue" }) => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm font-medium">{title}</p>
          <p className={`text-2xl font-bold text-${color}-600 mt-1`}>{value}</p>
          {subtitle && <p className="text-gray-500 text-xs mt-1">{subtitle}</p>}
        </div>
        <div className={`bg-${color}-100 p-3 rounded-lg`}>
          <Icon className={`text-${color}-600`} size={24} />
        </div>
      </div>
    </div>
  );

  const ProgressBar = ({ progress, color = "blue" }) => (
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div 
        className={`bg-${color}-600 h-2 rounded-full transition-all duration-300`}
        style={{ width: `${progress}%` }}
      ></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-2xl font-bold text-blue-600">Kodekalp</div>
              <div className="text-gray-400">|</div>
              <div className="text-lg text-gray-700">Employee Dashboard</div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-gray-600">Welcome back,</p>
                <p className="font-semibold text-gray-900">{employeeData.profile.name}</p>
              </div>
              <img 
                src={employeeData.profile.profileImage} 
                alt="Profile" 
                className="w-10 h-10 rounded-full border-2 border-blue-200"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Navigation Tabs */}
        <div className="flex gap-4 mb-8 overflow-x-auto pb-2">
          <TabButton id="overview" label="Overview" icon={Home} />
          <TabButton id="attendance" label="Attendance" icon={Clock} />
          <TabButton id="projects" label="Projects" icon={FileText} />
          <TabButton id="payroll" label="Payroll" icon={DollarSign} />
          <TabButton id="profile" label="Profile" icon={User} />
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard 
                icon={Clock} 
                title="Today's Hours" 
                value={employeeData.attendance.totalHours}
                subtitle="Checked in at 9:15 AM"
                color="green"
              />
              <StatCard 
                icon={CheckCircle} 
                title="Active Projects" 
                value="2" 
                subtitle="1 high priority"
                color="blue"
              />
              <StatCard 
                icon={Calendar} 
                title="Leaves Remaining" 
                value={employeeData.attendance.leavesRemaining}
                subtitle="Out of 30 total"
                color="orange"
              />
              <StatCard 
                icon={Award} 
                title="Performance" 
                value="4.8/5" 
                subtitle="Last review: Nov 2025"
                color="purple"
              />
            </div>

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Current Projects */}
              <div className="lg:col-span-2 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText size={20} />
                  Current Projects
                </h3>
                <div className="space-y-4">
                  {employeeData.projects.filter(p => p.status === 'In Progress').map((project, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">{project.name}</h4>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          project.priority === 'high' ? 'bg-red-100 text-red-700' :
                          project.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {project.priority}
                        </span>
                      </div>
                      <div className="mb-2">
                        <ProgressBar progress={project.progress} />
                      </div>
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>{project.progress}% Complete</span>
                        <span>Due: {project.deadline}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Team & Announcements */}
              <div className="space-y-6">
                {/* Team Members */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Users size={20} />
                    Team Members
                  </h3>
                  <div className="space-y-3">
                    {employeeData.team.map((member, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <img src={member.avatar} alt={member.name} className="w-8 h-8 rounded-full" />
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{member.name}</p>
                          <p className="text-gray-600 text-xs">{member.role}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Announcements */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <AlertCircle size={20} />
                    Announcements
                  </h3>
                  <div className="space-y-3">
                    {employeeData.announcements.map((announcement, index) => (
                      <div key={index} className="border-l-4 border-blue-500 pl-3">
                        <h4 className="font-medium text-gray-900 text-sm">{announcement.title}</h4>
                        <p className="text-gray-600 text-xs mt-1">{announcement.content}</p>
                        <p className="text-gray-500 text-xs mt-1">{announcement.date}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Attendance Tab */}
        {activeTab === 'attendance' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard 
                icon={CheckCircle} 
                title="Today's Status" 
                value={employeeData.attendance.todayStatus}
                subtitle={`In: ${employeeData.attendance.checkInTime}`}
                color="green"
              />
              <StatCard 
                icon={Clock} 
                title="Hours Today" 
                value={employeeData.attendance.totalHours}
                subtitle="Target: 8 hours"
              />
              <StatCard 
                icon={Calendar} 
                title="Monthly Attendance" 
                value={`${employeeData.attendance.monthlyAttendance}%`}
                subtitle="22/23 days present"
                color="blue"
              />
              <StatCard 
                icon={Coffee} 
                title="Leaves Used" 
                value={employeeData.attendance.leavesUsed}
                subtitle={`${employeeData.attendance.leavesRemaining} remaining`}
                color="orange"
              />
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Attendance Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-700 mb-3">This Week</h4>
                  <p className="text-2xl font-bold text-blue-600">{employeeData.attendance.weeklyHours}</p>
                  <p className="text-gray-600 text-sm">Total hours worked</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-700 mb-3">Quick Actions</h4>
                  <div className="space-y-2">
                    <button className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                      Request Leave
                    </button>
                    <button className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                      View Timesheet
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">All Projects</h3>
              <div className="space-y-4">
                {employeeData.projects.map((project, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-medium text-gray-900">{project.name}</h4>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          project.status === 'Completed' ? 'bg-green-100 text-green-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {project.status}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          project.priority === 'high' ? 'bg-red-100 text-red-700' :
                          project.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {project.priority}
                        </span>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="flex justify-between text-sm text-gray-600 mb-1">
                        <span>Progress</span>
                        <span>{project.progress}%</span>
                      </div>
                      <ProgressBar 
                        progress={project.progress} 
                        color={project.status === 'Completed' ? 'green' : 'blue'} 
                      />
                    </div>
                    <p className="text-gray-600 text-sm">Deadline: {project.deadline}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Payroll Tab */}
        {activeTab === 'payroll' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard 
                icon={DollarSign} 
                title="Basic Salary" 
                value={employeeData.payroll.basicSalary}
                subtitle="Monthly"
                color="green"
              />
              <StatCard 
                icon={TrendingUp} 
                title="Total Earnings" 
                value={employeeData.payroll.totalEarnings}
                subtitle="Including allowances"
                color="blue"
              />
              <StatCard 
                icon={DollarSign} 
                title="Net Salary" 
                value={employeeData.payroll.netSalary}
                subtitle="After deductions"
                color="purple"
              />
              <StatCard 
                icon={Calendar} 
                title="YTD Earnings" 
                value={employeeData.payroll.ytdEarnings}
                subtitle="Year to date"
                color="orange"
              />
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Salary Breakdown</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-medium text-gray-700 mb-4">Earnings</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Basic Salary</span>
                      <span className="font-medium">₹85,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">HRA</span>
                      <span className="font-medium">₹12,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Special Allowance</span>
                      <span className="font-medium">₹5,000</span>
                    </div>
                    <div className="border-t pt-3">
                      <div className="flex justify-between font-semibold">
                        <span>Total Earnings</span>
                        <span className="text-green-600">₹1,02,000</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-700 mb-4">Deductions</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">PF</span>
                      <span className="font-medium">₹8,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Income Tax</span>
                      <span className="font-medium">₹3,500</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Professional Tax</span>
                      <span className="font-medium">₹500</span>
                    </div>
                    <div className="border-t pt-3">
                      <div className="flex justify-between font-semibold">
                        <span>Total Deductions</span>
                        <span className="text-red-600">₹12,000</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-900">Net Salary</span>
                  <span className="text-2xl font-bold text-blue-600">₹90,000</span>
                </div>
                <p className="text-gray-600 text-sm mt-1">Last paid on: {employeeData.payroll.lastPayDate}</p>
              </div>
            </div>
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-start gap-6">
                <img 
                  src={employeeData.profile.profileImage} 
                  alt="Profile" 
                  className="w-24 h-24 rounded-full border-4 border-blue-200"
                />
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900">{employeeData.profile.name}</h2>
                  <p className="text-blue-600 font-medium">{employeeData.profile.position}</p>
                  <p className="text-gray-600">{employeeData.profile.department}</p>
                  <p className="text-gray-500 text-sm mt-2">Employee ID: {employeeData.profile.id}</p>
                </div>
                <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                  Edit Profile
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Mail className="text-gray-400" size={20} />
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-medium">{employeeData.profile.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="text-gray-400" size={20} />
                    <div>
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="font-medium">{employeeData.profile.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="text-gray-400" size={20} />
                    <div>
                      <p className="text-sm text-gray-600">Location</p>
                      <p className="font-medium">{employeeData.profile.location}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Work Information</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="text-gray-400" size={20} />
                    <div>
                      <p className="text-sm text-gray-600">Join Date</p>
                      <p className="font-medium">{employeeData.profile.joinDate}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <User className="text-gray-400" size={20} />
                    <div>
                      <p className="text-sm text-gray-600">Reporting Manager</p>
                      <p className="font-medium">{employeeData.profile.manager}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Award className="text-gray-400" size={20} />
                    <div>
                      <p className="text-sm text-gray-600">Years at Kodekalp</p>
                      <p className="font-medium">3.9 years</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeDashboard;